// File: src/App.jsx
import React, {
  Suspense,
  lazy,
  useEffect,
} from "react";

import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useParams,
} from "react-router-dom";

import "react-toastify/dist/ReactToastify.css";

/* ──────────────────────────────────────────────────────────────
 * Authentication / public pages
 * ────────────────────────────────────────────────────────────── */

import LoginPage from "./pages/Auth/LoginPage.jsx";
import RegisterPage from "./pages/Auth/RegisterPage.jsx";
import ForbiddenPage from "./pages/Auth/ForbiddenPage.jsx";
import ForgotPasswordPage from "./pages/Auth/ForgotPasswordPage.jsx";
import ResetPasswordPage from "./pages/Auth/ResetPasswordPage.jsx";

import UnauthorizedPage from "@/pages/UnauthorizedPage";

/* ──────────────────────────────────────────────────────────────
 * Security / layout
 * ────────────────────────────────────────────────────────────── */

import ProtectedRoute from "./utils/ProtectedRoute";
import AppShell from "@/components/Layout/AppShell";
import HomeRouter from "@/pages/HomeRouter";

/* ──────────────────────────────────────────────────────────────
 * Dashboards
 * ────────────────────────────────────────────────────────────── */

import DashboardPage from "@/pages/DashboardPage";
import StaffingDashboard from "@/pages/StaffingDashboard";

import ProcurementDashboard from "@/pages/Procurement/ProcurementDashboard";
import ProcurementGmBriefPage from "@/pages/Procurement/ProcurementGmBriefPage";
import ProcurementGMAssistantPage from "@/pages/Procurement/ProcurementGMAssistantPage";

import EventHistoryPage from "@/pages/EventHistoryPage";
import TaskDetailTestPage from "@/pages/TaskDetailTestPage";
import VendorDirectoryPage from "@/pages/VendorDirectoryPage";

/* ──────────────────────────────────────────────────────────────
 * Maintenance
 * ────────────────────────────────────────────────────────────── */

import MaintenanceBoard from "@/components/Maintenance/MaintenanceBoard";
import MaintenanceManagementBoard from "@/components/Maintenance/MaintenanceManagementBoard";
import MaintenanceManagementTaskPage from "@/components/Maintenance/MaintenanceManagementTaskPage";

/* ──────────────────────────────────────────────────────────────
 * Events
 * ────────────────────────────────────────────────────────────── */

import EventsDashboard from "@/pages/Event/EventsDashboard";
import EventDetailPage from "@/pages/Event/EventDetailPage";
import EventReportsDashboard from "@/pages/Event/EventReportsDashboard";
import EventReportViewer from "@/pages/Event/EventReportViewer";

import EventProcurementOverview from "@/components/event/EventProcurementOverview";
import EventStaffingOverview from "@/components/event/EventStaffingOverview";

import EventFormPage from "@/pages/Event/EventFormPage";
import EventSelection from "@/pages/Event/EventSelection.jsx";
import EventRevenueEntryPage from "@/pages/Event/EventRevenueEntryPage";

/* ──────────────────────────────────────────────────────────────
 * Planners
 * ────────────────────────────────────────────────────────────── */

import PlannersPage from "@/pages/Planner/PlannersPage";
import PlannerFormPage from "@/pages/Planner/PlannerFormPage";

/* ──────────────────────────────────────────────────────────────
 * Procurement
 * ────────────────────────────────────────────────────────────── */

import POMgmtDashboard from "@/pages/Procurement/POMgmtDashboard";
import ReceivingActivity from "@/pages/ReceivingActivity";
import POReceivingPage from "@/pages/POReceivingPage";

import DispatchedPOViewer from "@/pages/Procurement/DispatchedPOViewer";
import VendorSettlementPage from "@/pages/Procurement/VendorSettlementPage";
import VendorForecastAlerts from "@/components/Procurement/VendorForecastAlerts";
import VendorPaymentsPage from "@/components/Procurement/VendorPaymentsPage";

import POReceivingDashboard from "@/pages/Procurement/POReceivingDashboard";
import POItemDetailPage from "@/pages/Procurement/POItemDetailPage";

import ProcurementReportPanel from "@/components/Procurement/ProcurementReportPanel";
import POApprovalCentre from "@/pages/Procurement/POApprovalCentre";
import POTimelineLogPage from "@/pages/Procurement/POTimelineLogPage";
import NewPOForm from "@/components/Procurement/NewPOForm.jsx";

import ProcurementAIInsightsPanel from "@/components/Procurement/ProcurementAIInsightsPanel";

/* ──────────────────────────────────────────────────────────────
 * Inventory
 * ────────────────────────────────────────────────────────────── */

import InventoryDashboard from "@/pages/Inventory/InventoryDashboard";
import StockLevels from "@/pages/Inventory/StockLevels";
import StockMovements from "@/pages/Inventory/StockMovements";
import BinsAndLocations from "@/pages/Inventory/BinsAndLocations";
import AIInsights from "@/pages/Inventory/AIInsights";
import UsageLogs from "@/pages/Inventory/UsageLogs";
import Replenishment from "@/pages/Inventory/Replenishment";
import SupplierCatalogPage from "@/pages/Inventory/SupplierCatalogPage";

import MenuItemManager from "@/pages/Menu/MenuItemManager";
import MenuItemsPage from "@/pages/Menu/MenuItemsPage";

import StockItemsPage from "@/pages/Inventory/StockItemsPage";
import StockItemsManager from "@/pages/Inventory/StockItemsManager";

/* ──────────────────────────────────────────────────────────────
 * Staffing
 * ────────────────────────────────────────────────────────────── */

import StaffDirectoryPage from "@/pages/StaffDirectoryPage";

import ShiftApprovalPanel from "@/components/Shifts/ShiftApprovalPanel";
import ClockInOutPanel from "@/components/Staff/ClockInOutPanel";
import ClockInQRPage from "@/pages/Staff/ClockInQRPage";

import PayrollReportViewer from "@/components/Payroll/PayrollReportViewer";
import TodayRotaSnapshot from "@/components/Staff/TodayRotaSnapshot";
import StaffingAlertsPanel from "@/components/Staff/StaffingAlertsPanel";
import StaffInsightsTable from "@/components/Staff/StaffInsightsTable";
import AgencyAssignmentHistory from "@/components/Staff/AgencyAssignmentHistory";

import PayrollDashboard from "@/pages/PayrollDashboard";
import AgencyShiftBookingPage from "@/pages/AgencyShiftBookingPage";
import RotaGridPage from "@/pages/RotaGridPage";

/* ──────────────────────────────────────────────────────────────
 * Leave
 * ────────────────────────────────────────────────────────────── */

import MyLeaveRequestPage from "@/pages/Staff/MyLeaveRequestPage";
import LeaveApprovalPage from "@/pages/Staff/LeaveApprovalPage";

/* ──────────────────────────────────────────────────────────────
 * Invoices
 * ────────────────────────────────────────────────────────────── */

import InvoiceUpload from "@/components/Invoices/InvoiceUpload";
import InvoiceReviewPage from "@/pages/InvoiceReviewPage";
import InvoicePage from "@/pages/InvoicePage";
import InvoiceHistoryPage from "@/pages/Invoices/InvoiceHistoryPage";
import OperationalInvoiceIntelligence from "@/pages/Invoices/OperationalInvoiceIntelligence";
import InvoiceViewerPage from "@/pages/InvoiceViewerPage";
import InvoiceApprovalPage from "@/pages/Invoices/InvoiceApprovalPage";
import InvoiceList from "@/components/Invoices/InvoiceList";

import InvoicePayPage from "./pages/Invoices/InvoicePayPage";

/* ──────────────────────────────────────────────────────────────
 * Administration
 * ────────────────────────────────────────────────────────────── */

import AccessControlPanel from "@/pages/AccessControlPanel";
import CompaniesAdminPage from "@/pages/admin/CompaniesAdminPage";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminPendingUsers from "@/pages/admin/AdminPendingUsers";
import ClientAdminDashboard from "@/pages/admin/ClientAdminDashboard";

/* ──────────────────────────────────────────────────────────────
 * Lazy-loaded company administration
 * ────────────────────────────────────────────────────────────── */

const CompanyAdminHomePage = lazy(() =>
  import(
    "@/pages/admin/CompanyAdminHomePage.jsx"
  )
);

const CompanyDashboard = lazy(() => import('@/pages/company/CompanyDashboard.jsx'));
const BusinessReport = lazy(() => import('@/pages/reports/BusinessReport.jsx'));

const CompanyUsersAdminPage = lazy(() =>
  import(
    "@/pages/admin/CompanyUsersAdminPage.jsx"
  )
);

const CompanyCostCentresAdminPage = lazy(() =>
  import(
    "@/pages/admin/CompanyCostCentresAdminPage.jsx"
  )
);

const CompanyUnitsAdminPage = lazy(() =>
  import(
    "@/pages/admin/CompanyUnitsAdminPage.jsx"
  )
);

/* ──────────────────────────────────────────────────────────────
 * Transitional role groups
 *
 * IMPORTANT:
 *
 * These mirror the application's CURRENT role behavior.
 *
 * They are frontend UX gates only.
 * Backend authorization remains authoritative.
 *
 * Later we can migrate these to explicit permissions once the
 * backend permission model is fully established.
 * ────────────────────────────────────────────────────────────── */

const CLIENT_ADMIN_ROLES = [
  "admin",
  "finance",
  "manager",
  "developer",
];

const PROCUREMENT_MANAGEMENT_ROLES = [
  "admin",
  "manager",
  "developer",
];

const EVENT_MENU_ROLES = [
  "admin",
  "developer",
  "manager",
  "procurement",
  "inventory",
];

const MAINTENANCE_MANAGEMENT_ROLES = [
  "admin",
  "developer",
  "manager",
  "finance",
  "procurement",
];

/* ──────────────────────────────────────────────────────────────
 * Utility components
 * ────────────────────────────────────────────────────────────── */

function ScrollToTop() {
  const {
    pathname,
  } =
    useLocation();

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "auto",
    });

    const root =
      document.getElementById(
        "root"
      );

    if (
      root &&
      root.scrollTop
    ) {
      root.scrollTop =
        0;
    }

    if (
      document.body
        ?.scrollTop
    ) {
      document.body.scrollTop =
        0;
    }

    if (
      document.documentElement
        ?.scrollTop
    ) {
      document.documentElement.scrollTop =
        0;
    }
  }, [
    pathname,
  ]);

  return null;
}

function NotFound() {
  return (
    <h1 className="p-6 text-xl text-white">
      404 – Page Not Found
    </h1>
  );
}

function LazyFallback() {
  return (
    <div className="p-6 text-sm text-white/70">
      Loading…
    </div>
  );
}

function ProcurementAIInsightsPage() {
  return (
    <div className="p-6 max-w-7xl mx-auto text-white">
      <ProcurementAIInsightsPanel />
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────
 * Legacy redirect helpers
 * ────────────────────────────────────────────────────────────── */

function RedirectEventToEvents() {
  const {
    id,
  } =
    useParams();

  return (
    <Navigate
      to={
        id
          ? `/events/${id}`
          : "/events"
      }
      replace
    />
  );
}

function RedirectEventDetailsToEventsDetails() {
  const {
    id,
  } =
    useParams();

  return (
    <Navigate
      to={
        id
          ? `/events/${id}/details`
          : "/events"
      }
      replace
    />
  );
}

function RedirectEventReportViewToEventsReportView() {
  const {
    id,
  } =
    useParams();

  return (
    <Navigate
      to={
        id
          ? `/events/${id}/report-view`
          : "/events/reports"
      }
      replace
    />
  );
}

/* ──────────────────────────────────────────────────────────────
 * Application
 * ────────────────────────────────────────────────────────────── */

function App() {
  return (
    <>
      <ScrollToTop />

      <Routes>
        {/* ─────────────────────────────────────────────────────
         * Public authentication routes
         * ───────────────────────────────────────────────────── */}

        <Route
          path="/login"
          element={<LoginPage />}
        />

        <Route
          path="/register"
          element={<RegisterPage />}
        />

        <Route
          path="/forgot-password"
          element={<ForgotPasswordPage />}
        />

        <Route
          path="/reset-password"
          element={<ResetPasswordPage />}
        />

        <Route
          path="/forbidden"
          element={<ForbiddenPage />}
        />

        <Route
          path="/unauthorized"
          element={<UnauthorizedPage />}
        />

        {/* ─────────────────────────────────────────────────────
         * Public tokenized invoice-payment routes
         *
         * Backend token validation remains the security authority.
         * ───────────────────────────────────────────────────── */}

        <Route
          path="/invoice/pay/:token"
          element={<InvoicePayPage />}
        />

        {/* ─────────────────────────────────────────────────────
         * Entry routing
         *
         * HomeRouter waits for server-backed AuthProvider.ready.
         * ───────────────────────────────────────────────────── */}

        <Route
          path="/"
          element={<HomeRouter />}
        />

        <Route
          path="/app"
          element={<HomeRouter />}
        />

        {/* ─────────────────────────────────────────────────────
         * Authenticated application
         * ───────────────────────────────────────────────────── */}

        <Route
          element={
            <ProtectedRoute />
          }
        >
          <Route
            element={
              <AppShell />
            }
          >
            {/* ─────────────────────────────────────────────────
             * Platform administration
             *
             * platformOnly relies only on /auth/me-derived
             * is_platform_admin.
             * ───────────────────────────────────────────────── */}

            <Route
              path="/admin"
              element={
                <ProtectedRoute
                  platformOnly
                  redirectForbidden="/unauthorized"
                />
              }
            >
              <Route
                index
                element={
                  <AdminDashboard />
                }
              />

              <Route
                path="access-control"
                element={
                  <AccessControlPanel />
                }
              />

              <Route
                path="pending-users"
                element={
                  <AdminPendingUsers />
                }
              />

              <Route
                path="companies"
                element={
                  <CompaniesAdminPage />
                }
              />

              <Route
                path="companies/:companyId"
                element={
                  <Suspense
                    fallback={
                      <LazyFallback />
                    }
                  >
                    <CompanyAdminHomePage />
                  </Suspense>
                }
              />

              <Route
                path="companies/:companyId/users"
                element={
                  <Suspense
                    fallback={
                      <LazyFallback />
                    }
                  >
                    <CompanyUsersAdminPage />
                  </Suspense>
                }
              />

              <Route
                path="companies/:companyId/cost-centres"
                element={
                  <Suspense
                    fallback={
                      <LazyFallback />
                    }
                  >
                    <CompanyCostCentresAdminPage />
                  </Suspense>
                }
              />

              <Route
                path="companies/:companyId/units"
                element={
                  <Suspense
                    fallback={
                      <LazyFallback />
                    }
                  >
                    <CompanyUnitsAdminPage />
                  </Suspense>
                }
              />
            </Route>

            {/* ─────────────────────────────────────────────────
             * Client administration
             * ───────────────────────────────────────────────── */}

            <Route
              path="/client-admin"
              element={
                <ProtectedRoute
                  roles={
                    CLIENT_ADMIN_ROLES
                  }
                />
              }
            >
              <Route
                index
                element={
                  <ClientAdminDashboard />
                }
              />
            </Route>

            <Route element={<ProtectedRoute allowIf={({user, isPlatformAdmin}) => isPlatformAdmin || user?.role === 'company_director'} />}>
              <Route path="/company" element={<Suspense fallback={<LazyFallback />}><CompanyDashboard /></Suspense>} />
              <Route path="/company/:companyId/dashboard" element={<Suspense fallback={<LazyFallback />}><CompanyDashboard /></Suspense>} />
              <Route path="/company/reports" element={<Suspense fallback={<LazyFallback />}><BusinessReport companyMode /></Suspense>} />
              <Route path="/company/:companyId/reports" element={<Suspense fallback={<LazyFallback />}><BusinessReport companyMode /></Suspense>} />
            </Route>

            {/* ─────────────────────────────────────────────────
             * Main dashboard
             * ───────────────────────────────────────────────── */}

            <Route
              path="/dashboard"
              element={
                <DashboardPage />
              }
            />

            {/* ─────────────────────────────────────────────────
             * Planners
             * ───────────────────────────────────────────────── */}

            <Route
              path="/planners"
              element={
                <PlannersPage />
              }
            />

            <Route
              path="/planners/new"
              element={
                <PlannerFormPage />
              }
            />

            <Route
              path="/event/planners"
              element={
                <Navigate
                  to="/planners"
                  replace
                />
              }
            />

            <Route
              path="/event/planners/new"
              element={
                <Navigate
                  to="/planners/new"
                  replace
                />
              }
            />

            {/* ─────────────────────────────────────────────────
             * Procurement
             * ───────────────────────────────────────────────── */}

            <Route
              path="/procurement"
              element={
                <ProcurementDashboard />
              }
            />

            <Route
              element={
                <ProtectedRoute
                  roles={
                    PROCUREMENT_MANAGEMENT_ROLES
                  }
                />
              }
            >
              <Route
                path="/procurement/ai-insights"
                element={
                  <ProcurementAIInsightsPage />
                }
              />

              <Route
                path="/procurement/gm-brief"
                element={
                  <ProcurementGmBriefPage />
                }
              />

              <Route
                path="/procurement/gm-assistant"
                element={
                  <ProcurementGMAssistantPage />
                }
              />
            </Route>

            <Route
              path="/procurement/new-po"
              element={
                <NewPOForm />
              }
            />

            <Route
              path="/procurement/vendor-forecast-alerts"
              element={
                <VendorForecastAlerts />
              }
            />

            <Route
              path="/procurement/po-details/:poId"
              element={
                <POItemDetailPage />
              }
            />

            <Route
              path="/procurement/report"
              element={
                <ProcurementReportPanel />
              }
            />

             <Route
  path="/procurement/po-approval"
  element={
    <ProtectedRoute
      permission="procurement.po.approve"
      redirectForbidden="/dashboard"
    />
  }
>
  <Route
    index
    element={
      <POApprovalCentre />
    }
  />
</Route>

            <Route
              path="/procurement/po-timeline-log"
              element={
                <POTimelineLogPage />
              }
            />

            <Route
  element={
    <ProtectedRoute
      permission="procurement.receiving.view"
      redirectForbidden="/dashboard"
    />
  }
>
  <Route
    path="/receiving-activity"
    element={
      <ReceivingActivity />
    }
  />

  <Route
    path="/po-receiving/:poId"
    element={
      <POReceivingPage />
    }
  />

  <Route
    path="/po-receiving-dashboard"
    element={
      <POReceivingDashboard />
    }
  />

  <Route
    path="/procurement/po-receiving/:poId"
    element={
      <POReceivingPage />
    }
  />
</Route>

            <Route
              path="/vendor-settlement"
              element={
                <VendorSettlementPage />
              }
            />

            <Route
              path="/vendor-payments"
              element={
                <VendorPaymentsPage />
              }
            />

            

            <Route
  path="/po-approval"
  element={
    <Navigate
      to="/procurement/po-approval"
      replace
    />
  }
/>

            <Route
              path="/dispatched-pos"
              element={
                <DispatchedPOViewer />
              }
            />

            <Route
              path="/po-management"
              element={
                <POMgmtDashboard />
              }
            />

            {/* Procurement compatibility routes */}

            <Route
              path="/procurement/receiving"
              element={
                <Navigate
                  to="/po-receiving-dashboard"
                  replace
                />
              }
            />

            <Route
              path="/procurement/receiving/:poId"
              element={
                <Navigate
                  to="/po-receiving/:poId"
                  replace
                />
              }
            />

            <Route
              path="/procurement/receiving-activity"
              element={
                <Navigate
                  to="/receiving-activity"
                  replace
                />
              }
            />

            <Route
              path="/procurement/receiving-dashboard"
              element={
                <Navigate
                  to="/po-receiving-dashboard"
                  replace
                />
              }
            />

            <Route
              path="/procurement/po-receiving-dashboard"
              element={
                <Navigate
                  to="/po-receiving-dashboard"
                  replace
                />
              }
            />


            <Route
              path="/procurement/vendor-settlement"
              element={
                <Navigate
                  to="/vendor-settlement"
                  replace
                />
              }
            />

            <Route
              path="/procurement/vendor-payments"
              element={
                <Navigate
                  to="/vendor-payments"
                  replace
                />
              }
            />

            <Route
              path="/procurement/dispatched-pos"
              element={
                <Navigate
                  to="/dispatched-pos"
                  replace
                />
              }
            />

            <Route
              path="/procurement/po-management"
              element={
                <Navigate
                  to="/po-management"
                  replace
                />
              }
            />

            <Route
              path="/procurement/po-approval-page"
              element={
                <Navigate
                  to="/procurement/po-approval"
                  replace
                />
              }
            />

            {/* ─────────────────────────────────────────────────
             * Inventory
             * ───────────────────────────────────────────────── */}

            <Route
              path="/inventory"
              element={
                <InventoryDashboard />
              }
            />

            <Route
              path="/inventory/stock-levels"
              element={
                <StockLevels />
              }
            />

            <Route
              path="/inventory/stock-movements"
              element={
                <StockMovements />
              }
            />

            <Route
              path="/inventory/bins-locations"
              element={
                <BinsAndLocations />
              }
            />

            <Route
              path="/inventory/ai-insights"
              element={
                <AIInsights />
              }
            />

            <Route
              path="/inventory/usage-logs"
              element={
                <UsageLogs />
              }
            />

            <Route
              path="/inventory/replenishment"
              element={
                <Replenishment />
              }
            />

            <Route
              path="/inventory/supplier-catalog"
              element={
                <SupplierCatalogPage />
              }
            />

            <Route
              path="/inventory/menu-items"
              element={
                <MenuItemsPage />
              }
            />

            <Route
              path="/inventory/menu-builder"
              element={
                <MenuItemManager />
              }
            />

            <Route
              path="/menu-items"
              element={
                <Navigate
                  to="/inventory/menu-items"
                  replace
                />
              }
            />

            <Route
              path="/menu-builder"
              element={
                <Navigate
                  to="/inventory/menu-builder"
                  replace
                />
              }
            />

            <Route
              path="/menu/items"
              element={
                <Navigate
                  to="/inventory/menu-items"
                  replace
                />
              }
            />

            <Route
              path="/menu/items/*"
              element={
                <Navigate
                  to="/inventory/menu-items"
                  replace
                />
              }
            />

            <Route
              path="/inventory/stock-items"
              element={
                <StockItemsPage />
              }
            />

            <Route
              path="/inventory/stock-items-manager"
              element={
                <StockItemsManager />
              }
            />

            <Route
              path="/inventory/stock-items/manage"
              element={
                <StockItemsManager />
              }
            />

            <Route
              path="/inventory/stock-items/manage/*"
              element={
                <StockItemsManager />
              }
            />

            {/* ─────────────────────────────────────────────────
             * Vendors
             * ───────────────────────────────────────────────── */}

            <Route
              path="/vendors"
              element={
                <VendorDirectoryPage />
              }
            />

            <Route
              path="/vendor-directory"
              element={
                <Navigate
                  to="/vendors"
                  replace
                />
              }
            />

            {/* ─────────────────────────────────────────────────
             * Staffing
             * ───────────────────────────────────────────────── */}

            <Route
              path="/staff-directory"
              element={
                <StaffDirectoryPage />
              }
            />

            <Route
              path="/shift-approval"
              element={
                <ShiftApprovalPanel />
              }
            />

            <Route
              path="/clock-in-out"
              element={
                <ClockInOutPanel />
              }
            />

            <Route
              path="/staff/clock-in-qr"
              element={
                <ClockInQRPage />
              }
            />

            <Route
              path="/payroll-report"
              element={
                <PayrollReportViewer />
              }
            />

            <Route
              path="/today-rota"
              element={
                <TodayRotaSnapshot />
              }
            />

            <Route
              path="/staffing-alerts"
              element={
                <StaffingAlertsPanel />
              }
            />

            <Route
              path="/staffing-insights"
              element={
                <StaffInsightsTable />
              }
            />

            <Route
              path="/staffing-dashboard"
              element={
                <StaffingDashboard />
              }
            />

            <Route
              path="/agency-shifts"
              element={
                <AgencyShiftBookingPage />
              }
            />

            <Route
              path="/staffing/payroll-dashboard"
              element={
                <PayrollDashboard />
              }
            />

            <Route
              path="/staffing/rota-grid"
              element={
                <RotaGridPage />
              }
            />

            <Route
              path="/staffing/rota-grid/:eventId"
              element={
                <RotaGridPage />
              }
            />

            <Route
              path="/staffing/agency-assignment-history"
              element={
                <AgencyAssignmentHistory />
              }
            />

            {/* Leave */}

            <Route
              path="/staff/leave-request"
              element={
                <MyLeaveRequestPage />
              }
            />

            <Route
              path="/staff/leave-approvals"
              element={
                <LeaveApprovalPage />
              }
            />

            {/* Rota compatibility */}

            <Route
              path="/rota/event/:eventId"
              element={
                <RotaGridPage />
              }
            />

            <Route
              path="/rota/cost-centre/:costCentreId"
              element={
                <RotaGridPage />
              }
            />

            {/* ─────────────────────────────────────────────────
             * Events
             * ───────────────────────────────────────────────── */}

            <Route
              path="/events"
              element={
                <EventsDashboard />
              }
            />

            <Route
              path="/events/reports"
              element={
                <EventReportsDashboard />
              }
            />

            <Route
              path="/event/reports"
              element={
                <Navigate
                  to="/events/reports"
                  replace
                />
              }
            />

            <Route
              path="/event/manager-reports"
              element={
                <Navigate
                  to="/events/reports"
                  replace
                />
              }
            />

            <Route
              path="/events/new"
              element={
                <EventFormPage />
              }
            />

            <Route
              path="/events/:id/edit"
              element={
                <EventFormPage />
              }
            />

            <Route
              path="/event/new"
              element={
                <Navigate
                  to="/events/new"
                  replace
                />
              }
            />

            <Route
              path="/event/:id"
              element={
                <RedirectEventToEvents />
              }
            />

            <Route
              path="/event/:id/details"
              element={
                <RedirectEventDetailsToEventsDetails />
              }
            />

            <Route
              path="/event/events/:id"
              element={
                <RedirectEventToEvents />
              }
            />

            <Route
              path="/events/:id"
              element={
                <EventDetailPage />
              }
            />

            <Route
              path="/events/:id/details"
              element={
                <EventDetailPage />
              }
            />

            <Route
              path="/events/:eventId/report-view"
              element={
                <EventReportViewer />
              }
            />

            <Route
              path="/event/:id/report-view"
              element={
                <RedirectEventReportViewToEventsReportView />
              }
            />

            <Route
              path="/event/:id/revenue-entry"
              element={
                <EventRevenueEntryPage />
              }
            />

            <Route
              path="/event/:id/procurement-overview"
              element={
                <EventProcurementOverview />
              }
            />

            <Route
              path="/event/:id/staffing-overview"
              element={
                <EventStaffingOverview />
              }
            />

            <Route
              element={
                <ProtectedRoute
                  roles={
                    EVENT_MENU_ROLES
                  }
                />
              }
            >
              <Route
                path="/events/:id/menu"
                element={
                  <EventSelection />
                }
              />
            </Route>

            {/* ─────────────────────────────────────────────────
             * Maintenance
             * ───────────────────────────────────────────────── */}

            <Route
              path="/maintenance"
              element={
                <MaintenanceBoard />
              }
            />

            <Route
              path="/maintenance/tasks"
              element={
                <MaintenanceBoard />
              }
            />

            <Route
              path="/maintenance/dashboard"
              element={
                <MaintenanceBoard />
              }
            />

            <Route
              element={
                <ProtectedRoute
                  roles={
                    MAINTENANCE_MANAGEMENT_ROLES
                  }
                />
              }
            >
              <Route
                path="/maintenance/manage"
                element={
                  <MaintenanceManagementBoard />
                }
              />

              <Route
                path="/maintenance/manage/:taskId"
                element={
                  <MaintenanceManagementTaskPage />
                }
              />

              <Route
                path="/maintenance/admin"
                element={
                  <MaintenanceManagementBoard />
                }
              />

              <Route
                path="/admin-maintenance"
                element={
                  <MaintenanceManagementBoard />
                }
              />
            </Route>

            {/* ─────────────────────────────────────────────────
             * General reports / legacy
             * ───────────────────────────────────────────────── */}

            <Route
              path="/test-task-modal"
              element={
                <TaskDetailTestPage />
              }
            />

            <Route
              path="/financial-report"
              element={
                <Suspense fallback={<LazyFallback />}><BusinessReport /></Suspense>
              }
            />

            <Route path="/reports/business" element={<Suspense fallback={<LazyFallback />}><BusinessReport /></Suspense>} />

            <Route
              path="/event-history"
              element={
                <EventHistoryPage />
              }
            />

            <Route
              path="/agency-history"
              element={
                <AgencyAssignmentHistory />
              }
            />

            {/* ─────────────────────────────────────────────────
             * Invoices
             * ───────────────────────────────────────────────── */}

            <Route
              path="/invoices"
              element={
                <InvoiceList />
              }
            />

            <Route
              path="/invoice"
              element={
                <InvoicePage />
              }
            />

            <Route
              path="/invoice/upload"
              element={
                <InvoiceUpload />
              }
            />

            <Route
              path="/invoice/history"
              element={
                <InvoiceHistoryPage />
              }
            />

            <Route
              path="/invoice/detail/:invoiceId"
              element={
                <OperationalInvoiceIntelligence />
              }
            />

            <Route
              path="/invoice/review"
              element={
                <InvoiceReviewPage />
              }
            />

            <Route
              path="/invoice/review/:invoiceId"
              element={
                <InvoiceReviewPage />
              }
            />

            <Route
              path="/invoices/line-review"
              element={
                <Navigate
                  to="/invoice/review"
                  replace
                />
              }
            />

            <Route
              path="/invoice/view"
              element={
                <Navigate
                  to="/invoice/review"
                  replace
                />
              }
            />

            <Route
              path="/invoice/view/:invoiceId"
              element={
                <InvoiceViewerPage />
              }
            />

            <Route
              path="/invoice/approval"
              element={
                <InvoiceApprovalPage />
              }
            />

            <Route
              path="/invoice/pay"
              element={
                <InvoicePayPage />
              }
            />
          </Route>
        </Route>

        {/* ─────────────────────────────────────────────────────
         * Catch-all
         * ───────────────────────────────────────────────────── */}

        <Route
          path="*"
          element={
            <NotFound />
          }
        />
      </Routes>
    </>
  );
}

export default App;
